// Service for fetching data from external APIs
import productSlugs from "../data/productSlugs.json";

// Define types
interface ProductSpecs {
  processor?: string;
  memory?: string;
  storage?: string;
  display?: string;
  connectivity?: string[];
  power?: string;
  [key: string]: any; // For dynamic specs
}

interface Product {
  id: number;
  slug: string;
  name: string;
  description: string;
  category: string;
  image: string;
  specs?: ProductSpecs;
  features?: string[];
  price?: number;
  rating?: number;
  isNew?: boolean;
  isFeatured?: boolean;
  discount?: number;
}

interface Stat {
  value: number;
  suffix: string;
  label: string;
}

interface Testimonial {
  quote: string;
  name: string;
  business?: string;
  location?: string;
  rating?: number;
}

interface CompanyInfo {
  name: string;
  description: string;
  services: Record<string, string>[];
  contact: {
    address: string;
    phone: string;
    redirectPhone: string;
    email: string;
    redirectEmail: string;
    whatsapp?: string;
  };
  seoKeywords: string[];
  aboutUs: string;
  whyChooseUs: string[];
  aboutUsTitle: string;
  productsTitle: string;
  stats: Stat[];
  testimonials: Testimonial[];
}

// Mock data that would be replaced with actual API calls.
// Exported synchronously (companyInfoData/productsData) so pages can seed
// state without a loading flash — critical for prerender/hydration parity.
// Swap pages back to the async fetchers when a real API exists.
const companyInfoData: CompanyInfo = {
    name: "R. S. Enterprises",
    description:
      "Leading provider of ECR and POS solutions with a commitment to quality and customer satisfaction.",
    services: [
      {
        name: "ECR Machines",
        description:
          "Reliable Electronic Cash Register (ECR) machine solutions for efficient retail billing and business automation.",
      },
      {
        name: "POS Systems",
        description:
          "Advanced Point of Sale (POS) systems designed to streamline billing, inventory, and customer management for retail and hospitality businesses.",
      },
      {
        name: "Billing Software",
        description:
          "Easy-to-use billing software for businesses, with GST support, invoicing, and real-time reporting features.",
      },
      {
        name: "Retail Automation",
        description:
          "Complete retail automation solutions to optimize inventory, checkout, and store management processes.",
      },
      {
        name: "Payment Solutions",
        description:
          "Secure and flexible payment solutions supporting card, UPI, QR, and digital wallet integrations for seamless transactions.",
      },
    ],
    contact: {
      address:
        "1st Floor, Office No. 106, 107, I.J Complex, G-29, Near, Subhash Chowk, opp. Metro Pillar Number 42, Laxmi Nagar, New Delhi - 110092",
      phone: "+91-9013573235\n+91-9958044400",
      redirectPhone: "+91-9013573235",
      email: "billingecrpos@gmail.com\nop.tyagi1971@gmail.com",
      redirectEmail: "billingecrpos@gmail.com",
      whatsapp: "+919013573235",
    },
    seoKeywords: [
      "Billing Machine in Laxmi Nagar",
      "Billing Machine in Delhi",
      "Billing Machine in New Delhi",
      "Billing Roll",
      "Printer Roll",
      "Printing Stickers",
      "Billing Machine in India",
      "ECR Machines Delhi",
      "POS System India",
      "Retail Billing Solutions",
      "R S Enterprises POS",
      "Best ECR Machine Dealer",
      "Billing Machine",
      "Pos Systems",
      "Billing Software",
      "Retail Automation",
      "Payment Solutions",
      "Cash Drawer",
      "Printer",
      "Billing Hardware Repairs",
      "POS Terminal",
      "Billing Stationery",
      "Customized POS Solutions",
      "GST Billing",
      "ECR Machines Delhi",
      "ECR Machine Dealers in Delhi",
      "Best ECR Machine Dealer",
      "Buy Billing Machine Delhi",
      "Billing Machine India",
      "POS System India",
      "POS System Price India",
      "Best POS System for Retail India",
      "POS System for Small Shops Delhi",
      "POS Terminal India",
      "Retail Billing Solutions",
      "Retail Billing Software India",
      "Retail Billing Machine Supplier",
      "Retail Automation Solutions",
      "Customized POS Solutions India",
      "R S Enterprises POS",
      "R S Enterprises Billing Solutions",
      "Billing Software India",
      "GST Billing Software India",
      "GST Billing Machine Supplier",
      "Payment Solutions India",
      "POS Receipt Printer India",
      "Cash Drawer India",
      "POS Hardware Supplier India",
      "Billing Hardware Repairs Delhi",
      "POS Repairs and Service Delhi",
      "Billing Stationery Supplier India",
      "POS Machine Near Me",
      "Best POS System India 2025",
    ],
    aboutUsTitle: "Learn more about our company and what makes us different",
    aboutUs: `
      <p>Founded in 2011 in Delhi (India), R S Enterprises is a trusted wholesale trader and service provider of Electronic Cash Registers (ECR), Point of Sale (POS) systems, and billing solutions, trusted by businesses across India for over a decade. We specialize in the wholesale trading, distribution, and servicing of:</p>
      
      <ul style="margin: 16px 0; padding-left: 24px;">
        <li>Billing machines</li>
        <li>Cash drawers</li>
        <li>POS terminals</li>
        <li>Billing software for restaurants and retail</li>
        <li>Printer and billing hardware repairs</li>
      </ul>
      
      <p>Our product range includes top industry brands like <strong>Essae, WEP, Trucount, NGX, Balaji, Pixel, Unisys, and Casio</strong>, ensuring unmatched reliability, affordability, and long-term durability. We also offer billing stationery and customized POS solutions tailored to your business needs.</p>
      
      <p>With a commitment to customer satisfaction and operational excellence, R S Enterprises empowers businesses of all sizes to streamline their billing processes and enhance operational efficiency.</p>
    `,
    whyChooseUs: [
      "Proven since 2011 with deep domain expertise",
      "Strong vendor network ensuring authentic, high‑grade products",
      "Customized configurations to match your exact workflow",
      "Stringent quality checks before dispatch",
      "Prompt deliveries, competitive pricing & nationwide reach",
      "Dedicated support & quick turnaround on repairs",
    ],
    productsTitle:
      "Explore our range of high-quality ECR and POS solutions designed to streamline your business operations.",
    // PLACEHOLDER — verify/edit these figures before publishing
    stats: [
      { value: 14, suffix: "+", label: "Years in business" },
      { value: 8, suffix: "", label: "Brand partnerships" },
      { value: 17, suffix: "+", label: "Products in catalog" },
      { value: 1000, suffix: "+", label: "Businesses served" },
    ],
    // PLACEHOLDER TESTIMONIALS — replace with real customer quotes.
    // Shown only as a fallback when live Google reviews are unavailable.
    testimonials: [
      {
        quote:
          "We bought our billing machine and printer from R S Enterprises. Genuine products, fair price, and they helped us set everything up the same day.",
        name: "Rajesh Kumar",
        business: "Kirana Store",
        location: "Laxmi Nagar, Delhi",
      },
      {
        quote:
          "Our restaurant runs on their POS system with KOT billing. Whenever we need support, one phone call is enough. Very reliable team.",
        name: "Sunita Sharma",
        business: "Family Restaurant",
        location: "East Delhi",
      },
      {
        quote:
          "We order printers and billing rolls in bulk for our stores. Deliveries are always on time and the pricing is the best we have found.",
        name: "Amit Verma",
        business: "Retail Chain Purchaser",
        location: "Delhi NCR",
      },
    ],
};

const fetchCompanyInfo = async (): Promise<CompanyInfo> => companyInfoData;

// Sample product data (slugs are merged in from src/data/productSlugs.json below)
const rawProducts: Omit<Product, "slug">[] = [
  {
    id: 1,
    name: "Essae POS 815J",
    description:
      "A comprehensive Point of Sale system designed to accelerate your business with a winning combination of a powerful 10th Generation Intel processor and superfast M.2 NVMe SSD storage.",
    // price: 599.99,
    // rating: 4.5,
    image: "/images/products/pos815j.png",
    isFeatured: true,
    category: "POS Systems",
    isNew: false,
    // discount: 15,
    specs: {
      CPU: "Intel Elkhart Lake J6412, up to 2.60 GHz",
      "System Memory": "4GB DDR4 RAM 1 x SO-DIMM Slot (Expandable up to 32GB)",
      "Storage Memory": "128GB M.2 NVMe SSD or 500GB HDD",
      "Display Size": '38.1 cm (15") TFT LCD with LED Backlight, Anti-glare',
      "Resolution / Brightness": "1024 x 768 / 350 nits",
      "Touch Screen": "Projected Capacitive Touch (PCAP) - True Flat",
      "USB Ports": "6",
      "Serial Ports (DB9)": "3",
      Network: "1 x Gigabit LAN",
      "Display Port": "1 x HDMI (Independent)",
      "Power Adapter": "DC 12 V / 5 A, 60 W",
      "Dimensions (W x D x H)": "382 mm x 256 mm x 355 mm",
      "Net Weight": "5.3 kg (approx)",
      "Operating System":
        "Windows 10 IoT Enterprise LTSC 2019 (64 Bit), Linux (as per specific request and confirmation only)",
    },
    features: [
      "Intel 10th Generation Processor",
      "M.2 NVMe SSD for superfast performance",
      "True Flat PCAP Touch screen technology",
      '15" Anti-glare LED backlit display',
      "Wi-Fi + Bluetooth connectivity",
      "Gigabit LAN connectivity",
      "HDMI independent display port",
      "Windows 10 IoT Enterprise support",
      "Expandable RAM up to 32GB",
      "Audio input/output support",
    ],
  },
  {
    id: 2,
    name: "Essae POS 816J",
    description:
      "A comprehensive Point of Sale system designed to accelerate your business with a winning combination of a powerful 10th Generation Intel processor and superfast M.2 NVMe SSD storage.",
    // price: 349.99,
    // rating: 4.2,
    image: "/images/products/pos816j.png",
    category: "POS Systems",
    isNew: false,
    isFeatured: true,
    // discount: 10,
    specs: {
      Processor: "Intel Elkhart Lake J6412, up to 2.6GHz",
      Memory: "4GB DDR4 RAM (1 x SO-DIMM, expandable up to 32GB)",
      Storage: "128GB M.2 NVMe SSD or 256GB M.2 NVMe SSD",
      Display: '15.6" TFT LCD, 1366 x 768 resolution, 350 nits brightness',
      Touchscreen: "Projected Capacitive Touch (PCAP) – True Flat",
      "USB Ports": 6,
      "Serial Ports (DB9)": 3,
      Network: "1 x Gigabit LAN",
      "Display Output": "1 x HDMI (Independent)",
      Connectivity: ["Wi-Fi", "Bluetooth", "Ethernet"],
      Audio: "1 x Line Out & Mic In",
      "Operating Temperature": "5°C ~ 40°C",
      Power: "DC 12V / 5A, 60W",
      "Dimensions (W x D x H)": "382mm (W) x 220mm (D) x 337mm (H)",
      Weight: "5.3 kg (approx)",
      OS: ["Windows 10 IoT", "Linux (subject to confirmation)"],
    },
    features: [
      "Thermal Printer (PR-65 / PR-75) support",
      "Auto-cutter",
      "2D Barcode & QR code support",
      "Metal Cash Drawer with Lock & Key",
      "3 Track-OPOS Compliant MSR (USB)",
      "20 char x 2 lines Customer Display",
      "VESA-compliant Wall Mount",
      "Compact Design",
    ],
  },
  {
    id: 3,
    name: "Essae POS 865J",
    description:
      "The POS-865J is a compact all-in-one POS system with a built-in high-speed thermal printer, 15-inch anti-glare touch display, and versatile connectivity, ideal for retail and hospitality operations.",
    // price: 129.99,
    // rating: 4.7,
    image: "/images/products/pos865j.png",
    category: "POS Systems",
    isNew: false,
    isFeatured: true,
    specs: {
      Processor: "Intel Elkhart Lake J6412, up to 2.60GHz",
      Memory: "4GB DDR4 RAM (1 x SO-DIMM, expandable up to 32GB)",
      Storage: "128GB M.2 NVMe SSD or 500GB HDD",
      Display: '15" TFT LCD with LED Backlight, Anti-glare',
      Resolution: "1024 x 768",
      Brightness: "350 nits",
      Touchscreen: "Projected Capacitive Touch (PCAP) – True Flat",
      "USB Ports": 6,
      "Serial Ports (DB9)": 3,
      Network: "1 x Gigabit LAN",
      "Display Output": "1 x HDMI (Independent)",
      Connectivity: ["Wi-Fi", "Bluetooth", "Ethernet"],
      Audio: "1 x Line Out & Mic In",
      "Operating Temperature": "5°C ~ 40°C",
      Power: "DC 12V / 5A & 24V / 2.5A (60W)",
      "Dimensions (W x D x H)": "382mm (W) x 295mm (D) x 422mm (H)",
      "Built-in Printer":
        "Thermal printer, 79.5 ± 0.5 mm paper, 220 mm/sec, Auto-cutter, 2D Barcode: QR code support, Interface: USB",
      Weight: "9.5 kg (approx)",
      OS: [
        "Windows 10 IoT Enterprise LTSC 2019 (64-bit)",
        "Linux (on request)",
      ],
    },
    features: [
      "Built-in Thermal Printer",
      "Auto-cutter",
      "2D Barcode & QR code support",
      "Metal Cash Drawer with Lock & Key",
      "3 Track-OPOS Compliant MSR (USB)",
      "20 char x 2 lines Customer Display",
      "9.7-inch Second Non-touch Display",
      "VESA-compliant Wall Mount",
      "Compact All-in-One Design",
    ],
  },
  {
    id: 4,
    name: "Essae POS 415N/416N",
    description:
      "The POS-415N/POS-416N is a lightweight and compact 12th Gen Intel-powered POS system with a 15.6-inch True Flat PCAP touchscreen, high-speed printing support, and versatile connectivity for modern retail and hospitality needs.",
    // price: 199.99,
    // rating: 4.3,
    image: "/images/products/pos415-416n.png",
    category: "POS Systems",
    isNew: false,
    specs: {
      Processor: "Intel Alder Lake N97, up to 3.60GHz (12th Gen)",
      Memory: "4GB DDR4 RAM (expandable up to 16GB)",
      Storage: "128GB M.2 NVMe SSD (optional 256GB)",
      Display: '15.6" TFT LCD, 1366 x 768 resolution, 220 nits brightness',
      Touchscreen: "Projected Capacitive Touch (PCAP) – True Flat",
      "USB Ports": 5,
      "Serial Ports (DB9)": 3,
      Network: "1 x Gigabit LAN",
      "Display Output": "1 x HDMI (Independent)",
      Connectivity: ["Wi-Fi", "Bluetooth", "Ethernet"],
      Audio: "1 x Line Out & Mic In",
      "Operating Temperature": "5°C ~ 40°C",
      Power: "DC 12V / 5A, 60W",
      "Dimensions (W x D x H)": "382mm (W) x 220mm (D) x 337mm (H)",
      Weight: "5.3 kg (approx)",
      OS: ["Windows 10 IoT", "Linux (subject to confirmation)"],
    },
    features: [
      "Thermal Printer Support (80mm, 220mm/sec)",
      "Auto-cutter",
      "QR Code Support",
      "Metal Cash Drawer with Lock & Key",
      "3 Track-OPOS Compliant MSR (USB)",
      "20 char x 2 lines Customer Display",
      "VESA-compliant Wall Mount Kit",
      "Lightweight & Compact Design",
    ],
  },
  {
    id: 5,
    name: "Essae POS 615",
    description:
      "The POS-615 is a high-performance POS system available in both 11th Gen Intel Core i3 and i5 variants, featuring a 15-inch anti-glare True Flat touch display, high-speed printing compatibility, and robust connectivity, ideal for demanding retail and hospitality applications.",
    // price: 149.99,
    // rating: 4.1,
    image: "/images/products/pos615.png",
    category: "POS Systems",
    isNew: false,
    // discount: 10,
    specs: {
      processor:
        "Intel Core i3-1115G4 / Intel Core i5 (11th Gen), up to 4.10GHz, 6MB Cache",
      memory: "4GB DDR4 RAM (1 x SO-DIMM, expandable up to 32GB)",
      storage: "128GB M.2 NVMe SSD or 256GB M.2 NVMe SSD",
      display: '15" TFT LCD with LED Backlight, Anti-glare',
      resolution: "1024 x 768",
      brightness: "350 nits",
      touchscreen: "Projected Capacitive Touch (PCAP) – True Flat",
      usbPorts: 6,
      serialPorts: 3,
      network: "1 x Gigabit LAN",
      displayOutput: "1 x HDMI",
      connectivity: ["Ethernet", "Wi-Fi (via option)"],
      audio: "1 x Line Out & Mic In",
      operatingTemperature: "5°C ~ 40°C",
      storageTemperature: "-20°C ~ 55°C",
      power: "DC 12V / 5A, 60W",
      dimensions: [
        "4° tilt angle: 382mm (W) x 220mm (D) x 356mm (H)",
        "84° tilt angle: 382mm (W) x 294mm (D) x 300mm (H)",
      ],
      weight: "6.1 kg (approx)",
      os: ["Windows 10 IoT", "Linux (subject to confirmation)"],
    },
    features: [
      "Supports PR-65 / PR-75 Thermal Printers (79.5 ± 0.5 mm and 82.5 ± 0.5 mm paper)",
      "High-speed printing (220 mm/sec) with Auto-cutter",
      "2D Barcode & QR code support",
      "Metal Cash Drawer with Lock & Key",
      "3 Track-OPOS Compliant MSR (USB)",
      "20 char x 2 lines Customer Display",
      "9.7-inch Second Non-touch Display",
      "VESA-compliant Wall Mount Kit",
    ],
  },
  {
    id: 6,
    name: "PR-65 POS Printer",
    description:
      "High-speed direct thermal printer with 220 mm/sec output, multiple connectivity options (USB, Serial, Ethernet), and durable auto cutter rated for 1.5 million cuts.",
    // price: 179.99,
    // rating: 4.0,
    image: "/images/products/pr65.png",
    isFeatured: true,
    category: "Printers",
    isNew: false,
    // discount: 10,
    specs: {
      printingMethod: "Direct Thermal",
      printSpeed: "220 mm/sec (max)",
      paperWidth: "79 ± 0.5 mm",
      interfaces: "Serial, USB, Ethernet",
      cutter: "Full or Partial cut",
      printerHeadLife: "100 km",
      autoCuts: "1.5 million",
      powerSupply: "DC 24 V / 2.5 A",
      cashDrawerInterface: "RJ-11 Connector",
      softwareDrivers:
        "Windows, ESC/POS, OPOS, JPOS, CUPS, Virtual COM USB Driver, SDK (Windows, Android, iOS)",
      barcodeSupport:
        "QR Code, EAN-8, EAN-13, Code 39, Code 128, ITF, UPC-A, UPC-E, Codabar",
      ledIndicators: "Paper, Cover open",
      dimensions: "142 × 185 × 136 mm",
      weight: "1.025 kg",
    },
    features: [],
  },
  {
    id: 7,
    name: "PR-75 POS Printer",
    description:
      "Premium direct thermal printer with wider paper support, faster reliability specs, and enhanced sensors for paper low detection.",
    // price: 179.99,
    // rating: 4.0,
    image: "/images/products/pr75.png",
    category: "Printers",
    isNew: false,
    // discount: 10,
    specs: {
      printingMethod: "Direct Thermal",
      printSpeed: "220 mm/sec (max)",
      paperWidth: "82.5 ± 0.5 mm",
      interfaces: "Serial, USB, Ethernet",
      cutter: "Full or Partial cut",
      printerHeadLife: "160 km",
      autoCuts: "1.8 million",
      powerSupply: "DC 24 V / 1.75 A",
      cashDrawerInterface: "RJ-11 Connector",
      softwareDrivers:
        "Windows, ESC/POS, OPOS, JPOS, CUPS, Virtual COM USB Driver, SDK (Windows, Android, iOS)",
      barcodeSupport:
        "QR Code, EAN-8, EAN-13, Code 39, Code 128, ITF, UPC-A, UPC-E, Codabar",
      ledIndicators: "Paper end, Paper low, Cover open",
      dimensions: "152 × 204 × 150 mm",
      weight: "1.7 kg",
    },
    features: [],
  },
  {
    id: 8,
    name: "PR-55 POS Printer",
    description:
      "The Essae PR-55 is a compact, high-speed direct thermal receipt printer with USB and Ethernet connectivity, partial cut capability, and extensive barcode support—ideal for retail and POS applications.",
    // price: 179.99,
    // rating: 4.0,
    image: "/images/products/pr55.png",
    category: "Printers",
    isNew: false,
    // discount: 10,
    specs: {
      Model: "PR-55",
      "Printing method": "Direct Thermal",
      "Printing speed": "200 mm/sec (max)",
      "Paper width": "79.5 ± 0.5 mm",
      "Printer interface": "USB & Ethernet",
      Cutter: "Partial cut",
      Reliability: "Printer head life: 100 km / Auto Cutter: 1 Million cuts",
      "Power supply": "DC 24 V / 2.5 A",
      "Cash drawer interface": "RJ-11 Connector",
      "Software driver":
        "Windows drivers, ESC/POS, OPOS, JPOS, CUPS, Virtual COM USB, Android & iOS SDK",
      "Barcode printing support":
        "QR code, EAN-8, EAN-13, Code 39, Code 128, ITF, UPC-A, UPC-E, Codabar",
      Sensor: "Paper end, Paper low, Cover open",
      "Printer dimension (W x D x H) mm": "145 x 123.5 x 121.6",
      "Printer weight": "1.35 kg",
    },
    features: [],
  },
  {
    id: 9,
    name: "PR-95 POS Printer",
    description:
      "The Essae PR-95 is a compact, high-speed direct thermal receipt printer with USB and Ethernet connectivity, partial cut capability, and extensive barcode support—ideal for retail and POS applications.",
    // price: 179.99,
    // rating: 4.0,
    image: "/images/products/pr95.png",
    category: "Printers",
    isNew: false,
    // discount: 10,
    specs: {
      Model: "PR-95",
      "Printing method": "Direct Thermal",
      "Printing speed": "220 mm/sec (max)",
      "Paper width": "79.5 ± 0.5 mm",
      "Printer interface": "USB & Ethernet",
      Cutter: "Full or Partial cut",
      Reliability: "Printer head life: 150 km / Auto Cutter: 1.5 Million cuts",
      "Power supply": "DC 24 V / 1.5 A",
      "Cash drawer interface": "RJ-11 Connector",
      "Software driver":
        "Windows Drivers (7 & 10), ESC/POS, OPOS Driver, JPOS Driver, CUPS Driver, Virtual COM USB Driver, Android & iOS SDK.",
      "Barcode printing support":
        "QR code, EAN-8, EAN-13, Code 39, Code 128, ITF, UPC-A, UPC-E, Codabar",
      Sensor: "Paper end, Paper low, Cover open",
      "Printer dimension (W x D x H) mm": "145 x 123.5 x 121.6",
      "Printer weight": "1.75 kg",
    },
    features: [],
  },
  {
    id: 10,
    name: "HS-410A Cash Drawer",
    description:
      "Durable metal cash drawer with multiple bill and coin compartments, RJ-11 interface, and lock & key security, designed for POS integration.",
    // price: 179.99,
    // rating: 4.0,
    image: "/images/products/hs410a.png",
    category: "Cash Drawers",
    isNew: false,
    // discount: 10,
    specs: {
      Model: "HS-410A",
      Interface:
        "24 V DC / 1 A, RJ-11 Connector (compatible with PR-Series printer)",
      MOC: "Body-Metal, Tray-ABS Plastic, Clip-Metal",
      Tray: "5 bill, 8 coin",
      Security: "Lock & Key",
      Dimension: "410 (W) x 405 (D) x 90 (H) mm",
      "Software driver": "ESC/POS, OPOS, JPOS (Windows 7 & 10)",
    },
    features: [],
  },
  {
    id: 11,
    name: "MSR (OPOS)",
    description:
      "High-durability magnetic stripe reader with triple-track support, USB interface, and bi-directional swipe for reliable POS integration.",
    // price: 179.99,
    // rating: 4.0,
    image: "/images/products/msr-ops.png",
    category: "Accessories",
    isNew: false,
    // discount: 10,
    specs: {
      Model: "MSR (OPOS)",
      "Track Support": "3 Tracks (Track 1/2/3)",
      Interface: "USB type",
      "Swipe speed": "12.7cm to 139.7cm (5 to 55”) per sec, Bi-directional",
      "Head Life": "1,000,000 passes Minimum",
      Color: "Black",
      "Software Driver": "OPOS (Windows POS Ready 7 & Windows 10 IOT)",
    },
    features: [],
  },
  {
    id: 12,
    name: "2x20 Line CD",
    description:
      "USB-powered blue-backlit LCD for clear transaction details, supporting OPOS and JPOS standards.",
    // price: 179.99,
    // rating: 4.0,
    image: "/images/products/cd2x20.png",
    category: "Accessories",
    isNew: false,
    // discount: 10,
    specs: {
      Model: "2x20 Line Customer display",
      "Display Type": "Graphic LCD with Blue backlight",
      Characters: "20 Characters x 2 Lines",
      Interface: "USB",
      "Software Driver":
        "OPOS (Windows POS Ready 7 & Windows 10 IOT), JPOS (Windows POS Ready 7 & Windows 10 IOT)",
    },
    features: [],
  },
  {
    id: 13,
    name: "Second Display Non Touch",
    description:
      "9.7-inch LED-backlit TFT LCD secondary monitor with VGA input for customer-facing visuals in POS setups.",
    // price: 179.99,
    // rating: 4.0,
    image: "/images/products/second_display.png",
    category: "Accessories",
    isNew: false,
    // discount: 10,
    specs: {
      Model: "Second Display Non Touch",
      Display: "9.7 inch Non Touch",
      "LCD Type": "TFT LCD (LED backlight)",
      Resolution: "1024x768",
      Brightness: "300 cd/m²",
      "Display Interface": "VGA",
      "Display Input": "DC 12V",
      Color: "Black",
      "Software Driver":
        "OPOS (Windows POS Ready 7 & Windows 10 IOT), JPOS (Windows POS Ready 7 & Windows 10 IOT)",
    },
    features: [],
  },
  {
    id: 14,
    name: "WEP BP-JOY",
    description:
      "WeP BP-JOY Thermal Billing Printer – Fast, reliable 2-inch thermal billing printer with GST support, 1000 bills/day capacity, and standalone operation for retail and restaurants.",
    // price: 599.99,
    // rating: 4.5,
    image: "/images/products/bpjoy.png",
    isFeatured: false,
    category: "Billing Machine",
    isNew: false,
    // discount: 15,
    specs: {
      "Print Speed": "10 bills per minute",
      "Printable Width": "2 inches",
      "Paper Type": "Thermal roll paper",
      "Paper Width": "2 inches",
      Power: "220V AC, 47-53 Hz",
      Dimension: "127mm x 142mm x 242mm",
    },
    features: [
      "Prints up to 1000 bills/day",
      "Standalone billing printer – works without a PC",
      "Thermal printing technology",
      "Stores up to 1700 items",
      "2 inch bill printout – clear and quick",
      "Generates 8 types of reports",
      "Billing done alphabetically or via item code",
      "GST & Discount inclusion available",
      "Generates Daily, Monthly, and Yearly Sales Reports",
      "Stores data on items and bill amounts",
      "Displays shop’s name and message on bill",
      "Maximum billable quantity: 999,999",
      "High reliability with low cost of ownership",
      "Round off option (50 Paise/ Rupee)",
      "Price change support",
      "Coupon print availability",
    ],
  },
  {
    id: 15,
    name: "WEP BP-JOY PRO",
    description:
      "WeP BP-JOY Pro Thermal Billing Printer – High-speed, 2-inch retail billing printer with 1000 bills/day capacity, 15 user logins, GST-ready reports, and standalone operation for shops and restaurants.",
    // price: 599.99,
    // rating: 4.5,
    image: "/images/products/bpjoypro.png",
    category: "Billing Machine",
    isNew: false,
    // discount: 15,
    specs: {
      "Print Speed": "10 bills per minute",
      "Printable Width": "2 inches",
      "Paper Type": "Thermal roll paper",
      "Paper Width": "2 inches",
      Power: "220V AC, 47-53 Hz",
      Dimension: "127mm x 142mm x 242mm",
    },
    features: [
      "Prints up to 1000 bills/day",
      "15 user logins",
      "Standalone billing printer – works without a PC",
      "Thermal printing technology",
      "Stores up to 1100 items",
      "2 inch bill printout – prints quickly",
      "Generates 11 kinds of reports",
      "Billing done alphabetically or through item code",
      "GST & Discount inclusion as required",
      "Generates Daily, Monthly and Yearly Sales Report",
      "Stores data on items sold and bill amounts",
      "Displays shop’s name and message on bill",
      "Maximum billable quantity: 999,999",
      "High reliability with low cost of ownership",
      "Round off option – 50 Paise/ Rupee",
      "Price change supported",
      "Coupon print availability",
      "Reports copy to USB pen drive via USB port",
      "Data transfer from PC to billing printer through USB drive",
      "Tamil language printing option",
    ],
  },
  {
    id: 16,
    name: "WEP BP-JOY POS",
    description:
      "JOY POS Smart Billing & Inventory POS System – Android-based POS with professional invoicing, stock management, detailed reports, 10.1” touchscreen, 2GB RAM, and seamless device connectivity.",
    // price: 599.99,
    // rating: 4.5,
    image: "/images/products/bpjoypos.png",
    category: "Billing Machine",
    isNew: false,
    // discount: 15,
    specs: {
      Processor: "1.30 GHz",
      RAM: "2GB",
      ROM: "16GB (Expandable)",
      Display: "10.1 inch",
      "Screen Resolution": "1280 × 800 pixels",
      Printer: "1 inch WeP TH400+",
      WiFi: "Yes",
      "Bluetooth / WiFi": "Yes",
      "OS Version": "Android Oreo 8.1",
      "Battery Backup": "Yes",
      "External Ports": "4 USB Ports",
    },
    features: [
      "Print and share professional invoices",
      "Add business logo to invoices",
      "Go paperless – send invoices, pay statements, and reports via email",
      "Send automatic SMS transaction messages",
      "Import bulk items from Excel",
      "Profit reports for monitoring earnings",
      "Categorize products by Department, Category, Brand, Discount",
      "Low stock alerts for timely inventory management",
      "Track business revenue from stock data",
      "Fully customizable application with enable/disable features",
      "Cloud server backup via JOYPOS Secured Cloud Service",
      "Detailed and summary transaction reports",
      "Employee reporting and performance tracking",
      "Customer passbook – complete transaction history",
      "Dashboard for business overview – sales, top products, inventory",
      "External device connectivity (Barcode scanner, weighing scale, cash drawer, keyboard, laser/Bluetooth/WiFi printer)",
      "Hot keys for favorite items quick launch",
      "Customer support feature built-in",
    ],
  },
  {
  id: 17,
  name: "Trucount T90",
  description:
    "Trucount T90 Electronic Cash Register – Restaurant & retail billing machine with KOT/KMC multi-company support, choice of fast DM or 150mm/sec thermal printer, 2000 PLUs, table management, and Pen Drive backup. Available in DM and TP models.",
  // price: 599.99,
  // rating: 4.5,
  // TODO: supply the real trucountt90.png photo — falls back to placeholder.png via onError until then
  image: "/images/products/trucountt90.png",
    isFeatured: false,
  category: "Billing Machine",
  isNew: false,
  // discount: 15,
  specs: {
    "Printer (DM Model)": "Dot Matrix, 4.1 lines/sec",
    "Printer (TP Model)": "Thermal, 150mm/sec",
    "Operator Display": "4 line × 20 characters",
    "Customer Display": "2 line × 16 characters",
    Keyboard: "63-key alphanumeric",
    "USB Host": "Pen Drive (front) & Barcode Scanner (rear)",
    "USB Device": "PC Linking (USB-B)",
    "External Ports": "Serial / Parallel / USB for 40/80-col printer",
    "Cash Drawer": "Dedicated output port",
    Battery: "In-built 11.1V 2200mAh Li-ion (optional) / External 12V 9Ah",
    Sensors: "Paper sensor & printer roller sensor",
    Connectivity: "RS232 PC Linking, USB, Weighing Scale, Barcode Scanner",
    "PLU Capacity": "2000 PLUs",
    "HOT PLUs": "78 (26 direct + 26 Shift1 + 26 Shift2)",
    "Item Code": "13 Digit",
    "Departments / Salesmen / Cashiers": "50 / 50 / 6",
    "Tax Rates": "10 programmable (VAT or ADD ON)",
    "Bill Summary": "Up to 1500 bills",
    "Credit Records": "250 parties / 750 transactions",
    "Items per Bill": "150",
    "SMS Facility": "Optional",
  },
  features: [
    "KOT (Kitchen Order Ticket) billing with table management",
    "KOT Multi-Company (KMC) – up to 4 companies and 4 kitchens",
    "Bar / liquor billing with peg and bottle stock handling",
    "Table transfer and merge facility",
    "Pending KOT up to 1500 items and 750 tables",
    "Split payment / tendering with change computation",
    "Bill park (hold) facility for 2 bills",
    "Partial item cancellation in bill",
    "Two sale prices, purchase price, MRP, and open price entry",
    "Discount and charge system (preset % and value based)",
    "Programmable service charge and service tax",
    "Five types of bill amount rounding",
    "Reverse calculation mode for sweet shops",
    "Advance receipt and advance adjustment function",
    "Stub / Duplicate Bill / Delivery Challan options",
    "10 header lines and 5 footer lines, two font sizes",
    "Cashier-wise AC / Non-AC sale break-up",
    "Variety of reports – daily/monthly PLU sale, financial, tax, cashier, inventory",
    "Historical reports – 31 days, 12 months, 10 years",
    "All data and item backup/restore via Pen Drive",
    "Reports saved to Pen Drive in RTF format (viewable in MS Word)",
    "Sale data update to PC via Pen Drive or RS232 linking",
    "Weighing scale attachment (front or rear)",
    "USB barcode scanner support",
    "Item search by alpha name during billing",
    "Optional SMS facility for report alerts",
    "Multi-language alpha table and preset alpha messages",
    "Credit party management with outstanding tracking",
  ],
},
];

// Merge URL slugs (single source of truth shared with the sitemap/prerender scripts)
const slugById = new Map(productSlugs.map(({ id, slug }) => [id, slug]));

const products: Product[] = rawProducts.map((product) => {
  const slug = slugById.get(product.id);
  if (!slug) {
    throw new Error(`Missing slug for product id ${product.id} in productSlugs.json`);
  }
  return { ...product, slug };
});

// Simulate API call to fetch products
const fetchProducts = async (): Promise<Product[]> => {
  // In a real app, this would be an actual API call
  return Promise.resolve(products);
};

const fetchProductBySlug = async (slug: string): Promise<Product | undefined> => {
  return Promise.resolve(products.find((product) => product.slug === slug));
};

// Same-category products first, padded with others to reach the limit
const fetchRelatedProducts = async (
  product: Product,
  limit = 4,
): Promise<Product[]> => {
  const sameCategory = products.filter(
    (p) => p.category === product.category && p.id !== product.id,
  );
  const others = products.filter(
    (p) => p.category !== product.category && p.id !== product.id,
  );
  return Promise.resolve([...sameCategory, ...others].slice(0, limit));
};

// Export functions and types separately
export {
  fetchCompanyInfo,
  fetchProducts,
  fetchProductBySlug,
  fetchRelatedProducts,
  companyInfoData,
};
export { products as productsData };

export type { CompanyInfo, Product, ProductSpecs, Stat, Testimonial };
